/**
 * Help-center section registry.
 *
 * Each entry is rendered both in the left-nav of `/help` AND as its own
 * deep-linkable page at `/help/:slug`.  The order field controls the
 * sidebar order — keep it stable, append new sections to the bottom
 * rather than re-numbering so deep links don't rot.
 *
 * Markdown lives in `./content/<slug>.js` (plain JS modules that export
 * the markdown string).  We chose `.js` over `.md` to sidestep CRA's
 * webpack config — no raw-loader / asset-module changes needed.
 */
import gettingStarted from "./content/getting-started.js";
import dashboardOverview from "./content/dashboard-overview.js";
import runningAWorkflow from "./content/running-a-workflow.js";
import workflowsLibrary from "./content/workflows-library.js";
import procurementWorkflows from "./content/procurement-workflows.js";
import personas from "./content/personas.js";
import foldersAndRunNumbers from "./content/folders-and-run-numbers.js";
import autoGenerationPipeline from "./content/auto-generation-pipeline.js";
import sharingDemos from "./content/sharing-demos.js";
import troubleshooting from "./content/troubleshooting.js";
import glossaryAndFaq from "./content/glossary-and-faq.js";
import cookieAuthInDevtools from "./content/cookie-auth-in-devtools.js";
import azureSpeechSetup from "./content/azure-speech-setup.js";
import externalSharingComingSoon from "./content/external-sharing-coming-soon.js";
import sharingDemosExternally from "./content/sharing-demos-externally.js";
import adminSharesAndAudit from "./content/admin-shares-and-audit.js";
import emailDeliverabilityAndSuppressions from "./content/email-deliverability-and-suppressions.js";
import organizingWorkflowsWithFolders from "./content/organizing-workflows-with-folders.js";

export const HELP_SECTIONS = [
  {
    slug: "getting-started",
    title: "Getting Started",
    blurb: "Sign in, your role, and what Eyvo Demo Agent can do.",
    icon: "Rocket",
    order: 1,
    content: gettingStarted,
  },
  {
    slug: "dashboard-overview",
    title: "Dashboard Overview",
    blurb: "Workflows, Demo Runs, History, Folders — where everything lives.",
    icon: "LayoutDashboard",
    order: 2,
    content: dashboardOverview,
  },
  {
    slug: "running-a-workflow",
    title: "Running a Workflow",
    blurb: "Variables, pacing, cursor toggle, and auto-pipeline.",
    icon: "Play",
    order: 3,
    content: runningAWorkflow,
  },
  {
    slug: "workflows-library",
    title: "Workflows Library",
    blurb: "Browsing the library, immutable versions, custom workflows.",
    icon: "Library",
    order: 4,
    content: workflowsLibrary,
  },
  {
    slug: "procurement-workflows",
    title: "The 3 Procurement Workflows",
    blurb: "Free-form, Catalog Item, and PunchOut — what each demonstrates.",
    icon: "ClipboardList",
    order: 5,
    content: procurementWorkflows,
  },
  {
    slug: "personas",
    title: "Persona-Targeted Narration",
    blurb: "Tailor the story for CFO, Procurement, IT Director, or general.",
    icon: "Users",
    order: 6,
    content: personas,
  },
  {
    slug: "folders-and-run-numbers",
    title: "Folders & Run Numbers",
    blurb: "Organize runs, share folders, and the global 4-digit Run #.",
    icon: "FolderTree",
    order: 7,
    content: foldersAndRunNumbers,
  },
  {
    slug: "auto-generation-pipeline",
    title: "Auto-Generation Pipeline",
    blurb: "GPT-5.2 narration → ElevenLabs voice → ffmpeg-stitched MP4.",
    icon: "Sparkles",
    order: 8,
    content: autoGenerationPipeline,
  },
  {
    slug: "sharing-demos",
    title: "Sharing Demos",
    blurb: "Public links, expiry, and what the recipient sees.",
    icon: "Share2",
    order: 9,
    content: sharingDemos,
  },
  {
    slug: "troubleshooting",
    title: "Troubleshooting",
    blurb: "What to do when a step fails or Eyvo's UI changes.",
    icon: "LifeBuoy",
    order: 10,
    content: troubleshooting,
  },
  {
    slug: "glossary-and-faq",
    title: "Glossary & FAQ",
    blurb: "Definitions, recurring questions, and where to go next.",
    icon: "BookOpen",
    order: 11,
    content: glossaryAndFaq,
  },
  {
    slug: "cookie-auth-in-devtools",
    title: "Verifying Cookie Auth in DevTools",
    blurb: "Confirm HttpOnly session, CSRF token, and the missing localStorage JWT.",
    icon: "ShieldCheck",
    order: 12,
    content: cookieAuthInDevtools,
  },
  {
    slug: "azure-speech-setup",
    title: "Azure Speech — Voiceover Setup",
    blurb: "Add AZURE_SPEECH_KEY + region, restart backend, switch the workflow voice.",
    icon: "Mic",
    order: 13,
    content: azureSpeechSetup,
  },
  {
    slug: "external-sharing-coming-soon",
    title: "External sharing — invitation-only is coming",
    blurb: "Why public shares have been retired and what's replacing them.",
    icon: "Mail",
    order: 14,
    content: externalSharingComingSoon,
  },
  {
    slug: "sharing-demos-externally",
    title: "Sharing demos externally (invitation-only)",
    blurb: "Email invites, watermarked player, audit timeline, revoke + resend.",
    icon: "Mail",
    order: 15,
    content: sharingDemosExternally,
  },
  {
    slug: "admin-shares-and-audit",
    title: "Admin Shares & Audit",
    blurb: "Global /shares dashboard, bulk revoke/resend, per-invitation drill-down.",
    icon: "ShieldCheck",
    order: 16,
    content: adminSharesAndAudit,
  },
  {
    slug: "email-deliverability-and-suppressions",
    title: "Email Deliverability & Suppressions",
    blurb: "Undeliverable, spam_reported, suppressed — what they mean and how to override.",
    icon: "Mail",
    order: 17,
    content: emailDeliverabilityAndSuppressions,
  },
  {
    slug: "organizing-workflows-with-folders",
    title: "Organizing workflows with folders",
    blurb: "Folder rail, inline chips, and drag-and-drop with Shift = move.",
    icon: "FolderTree",
    order: 18,
    content: organizingWorkflowsWithFolders,
  },
];

export function getSectionBySlug(slug) {
  return HELP_SECTIONS.find((s) => s.slug === slug) || null;
}
